"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { BookOpen, Cog, FileText, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
const guidanceCards = [{
  icon: FileText,
  tag: "SPEC-01",
  title: "Product Questions",
  description: "Ask about the information currently available for a particular machine.",
  note: "Include model & SKU"
}, {
  icon: BookOpen,
  tag: "SPEC-02",
  title: "Setup Information",
  description: "Use the Setup & Safety Guide and the documentation supplied for the exact product.",
  note: "Review technical manuals"
}, {
  icon: Cog,
  tag: "SPEC-03",
  title: "Replacement Parts",
  description: "Browse Parts & Accessories and confirm compatibility before ordering components.",
  note: "Verify visible serial tags"
}];
export default function ProductSupport() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="productinformationsupport" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 md:py-24 border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Spec Block */}
        <div className="border border-border bg-card p-6 sm:p-8 md:p-10 mb-8 sm:mb-12 shadow-xs">
          <div className="flex flex-wrap items-center justify-between gap-2 pb-4 mb-6 border-b border-border text-xs font-mono text-muted-foreground uppercase tracking-widest">
            <span>Section // Technical Support & Documentation</span>
            <span>Commercial Equipment Sales Only</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-start">
            <div className="lg:col-span-5">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground leading-none">
                Product Information &amp; Support
              </h2>
            </div>

            <div className="lg:col-span-7 space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
              <p>
                Questions about a mechanical bull are easiest to answer when they relate to a specific product.
                Include the product name, SKU and relevant details when contacting Mechanical Bull Shop.
              </p>
              <p>
                For setup or parts questions, also provide the machine model and any available component
                information. If you are trying to identify a replacement part, photographs and visible labels can
                provide useful context.
              </p>
            </div>
          </div>
        </div>

        {/* 3-Card Guidance Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 sm:mb-14">
          {guidanceCards.map((card, index) => {
          const IconComponent = card.icon;
          return <motion.div key={card.tag} variants={itemVariants} data-index={index} className="h-full">
                <Card className="h-full rounded-none border border-border bg-card hover:border-foreground/40 transition-[border-color] duration-200 flex flex-col justify-between py-6 shadow-xs">
                  <CardHeader className="gap-3 pb-3">
                    <div className="flex items-center justify-between">
                      <div className="size-10 flex items-center justify-center bg-muted text-foreground border border-border">
                        <IconComponent className="size-5" />
                      </div>
                      <span className="font-mono text-xs text-muted-foreground tracking-wider uppercase">
                        {card.tag}
                      </span>
                    </div>
                    <CardTitle className="font-serif text-2xl font-bold uppercase tracking-wide text-foreground pt-2">
                      {card.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-normal">
                      {card.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-4 border-t border-border/60">
                    <div className="flex items-center justify-between text-xs font-mono text-muted-foreground">
                      <span>Requirement:</span>
                      <span className="text-foreground">{card.note}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Industrial Action Bar */}
        <div className="border border-border bg-muted p-6 sm:p-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground block">
              Reference Catalog &amp; Technical Specs
            </span>
            <p className="text-sm text-foreground font-medium">
              Consult equipment procedures or view current compatible inventory.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Button asChild variant="secondary" className="rounded-none font-mono text-xs tracking-wider uppercase h-11 px-6 border border-secondary hover:bg-secondary/90 transition-colors">
              <Link to="/SetupSafetyAndMaintenancePage">
                Read Setup &amp; Safety
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>

            <Button asChild className="rounded-none font-mono text-xs tracking-wider uppercase h-11 px-6 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
              <Link to="/MechanicalBullPartsAndAccessoriesPage">
                Browse Parts
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}